import { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatCOP, formatDate } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';
import { AdminPromoOffers } from '@/components/AdminPromoOffers';
import { Search, Tag, Plus, Pencil, Trash2, Percent, DollarSign, X } from 'lucide-react';
import { Pagination, usePagination } from '@/components/Pagination';

// Sección de administración para configurar promociones de productos:
// tipo (% o COP fijo), valor, activar/desactivar y ventana de fechas opcional.
// Los descuentos se guardan en el campo `promotion` del producto y son
// separados del sponsorship (visibilidad).
export default function AdminPromotions() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'true' | 'false'
  const [editing, setEditing] = useState(null); // { product_id, name, price, promotion? }
  const [saving, setSaving] = useState(false);

  useSEO({
    title: 'Promociones | Administración',
    description: 'Configura descuentos por producto: porcentaje o valor fijo, ventana de fechas y activación.',
    canonical: '/admin/promociones',
  });

  const fetchData = useCallback(async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (activeFilter !== 'all') params.active = activeFilter;
    try {
      const { data } = await api.get('/admin/promotions', { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error('No se pudieron cargar las promociones');
    }
  }, [q, activeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => items, [items]);
  const { pageItems, page, setPage, totalPages, total } = usePagination(filtered);

  const clearFilters = () => { setQ(''); setActiveFilter('all'); };
  const hasFilters = !!q.trim() || activeFilter !== 'all';

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Administración</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">
              Promociones de productos
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Configura descuentos por producto (porcentaje o monto fijo). Puedes activar/desactivar y definir vigencia.
            </p>
          </div>
          <Button
            data-testid="admin-promotions-new-btn"
            onClick={() => setEditing({ product_id: null })}
            className="rounded-none bg-orange-500 hover:bg-orange-600 text-slate-900"
          >
            <Plus className="mr-2 h-4 w-4" /> Nueva promoción
          </Button>
        </div>

        <div className="mt-6 border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-[1fr,180px,auto] gap-3 items-end">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Buscar producto</label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                data-testid="admin-promotions-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nombre, referencia o marca…"
                className="pl-9 rounded-none"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Estado</label>
            <Select value={activeFilter} onValueChange={setActiveFilter}>
              <SelectTrigger data-testid="admin-promotions-status" className="rounded-none mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="true">Activas</SelectItem>
                <SelectItem value="false">Inactivas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {hasFilters && (
            <Button variant="outline" onClick={clearFilters} className="rounded-none h-10">
              <X className="h-4 w-4 mr-1" /> Limpiar
            </Button>
          )}
        </div>

        <div className="mt-4 border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Producto</th>
                <th className="px-4 py-3">Precio</th>
                <th className="px-4 py-3">Descuento</th>
                <th className="px-4 py-3">Vigencia</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageItems.map((p) => {
                const promo = p.promotion || {};
                const discount = promo.type === 'percent'
                  ? `${promo.value}%`
                  : promo.type === 'fixed' ? formatCOP(promo.value) : '—';
                return (
                  <tr key={p.product_id} data-testid={`admin-promo-row-${p.product_id}`} className="row-hover">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{p.name}</div>
                      <div className="text-xs text-slate-500">{p.brand} · {p.reference}</div>
                    </td>
                    <td className="px-4 py-3 font-bold text-slate-900">{formatCOP(p.price)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-orange-700 font-bold">
                        {promo.type === 'percent' ? <Percent className="h-3 w-3" /> : <DollarSign className="h-3 w-3" />}
                        {discount}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {(promo.starts_at || promo.ends_at) ? (
                        <>
                          <div>Desde: {promo.starts_at ? formatDate(promo.starts_at) : '—'}</div>
                          <div>Hasta: {promo.ends_at ? formatDate(promo.ends_at) : '—'}</div>
                        </>
                      ) : (
                        <span className="text-slate-400">Sin fechas</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        data-testid={`admin-promo-state-${p.product_id}`}
                        className={`rounded-none border ${promo.active
                          ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                          : 'bg-slate-100 text-slate-700 border-slate-200'}`}
                      >
                        {promo.active ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          data-testid={`admin-promo-edit-${p.product_id}`}
                          size="sm" variant="outline" className="rounded-none"
                          onClick={() => setEditing({
                            product_id: p.product_id, name: p.name, price: p.price,
                            promotion: promo.type ? promo : null,
                          })}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          data-testid={`admin-promo-delete-${p.product_id}`}
                          size="sm" variant="outline"
                          className="rounded-none border-rose-300 text-rose-700 hover:bg-rose-50"
                          onClick={async () => {
                            if (!window.confirm(`¿Eliminar la promoción de "${p.name}"?`)) return;
                            try {
                              await api.delete(`/admin/products/${p.product_id}/promotion`);
                              toast.success('Promoción eliminada');
                              fetchData();
                            } catch { toast.error('No se pudo eliminar'); }
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {pageItems.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-500">
                    <Tag className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                    Sin promociones. Haz clic en "Nueva promoción" para crear la primera.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} total={total} setPage={setPage} testIdPrefix="admin-promotions-pag" />

        <PromotionDialog
          open={!!editing}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchData(); }}
          saving={saving}
          setSaving={setSaving}
        />

        <AdminPromoOffers />
      </div>
    </Layout>
  );
}

// Dialog para crear/editar promoción. Cuando `initial.product_id` está vacío
// muestra un buscador de productos; cuando ya existe muestra los campos de
// tipo/valor/vigencia/activar.
function PromotionDialog({ open, initial, onClose, onSaved, saving, setSaving }) {
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [form, setForm] = useState({ type: 'percent', value: '', active: true, starts_at: '', ends_at: '' });

  useEffect(() => {
    if (!open) return;
    if (initial?.product_id) {
      setSelectedProduct({ product_id: initial.product_id, name: initial.name, price: initial.price });
      const p = initial.promotion;
      setForm({
        type: p?.type || 'percent',
        value: p?.value ?? '',
        active: p?.active ?? true,
        starts_at: p?.starts_at ? String(p.starts_at).substring(0, 10) : '',
        ends_at: p?.ends_at ? String(p.ends_at).substring(0, 10) : '',
      });
    } else {
      setSelectedProduct(null);
      setForm({ type: 'percent', value: '', active: true, starts_at: '', ends_at: '' });
      setProductSearch('');
      setProductResults([]);
    }
  }, [open, initial]);

  useEffect(() => {
    if (!open || selectedProduct) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/admin/promotions/products/search', {
          params: { q: productSearch, limit: 15 },
        });
        setProductResults(data.items || []);
      } catch { setProductResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [productSearch, open, selectedProduct]);

  const save = async () => {
    if (!selectedProduct) { toast.error('Selecciona un producto'); return; }
    const val = parseFloat(form.value);
    if (!(val > 0)) { toast.error('El valor debe ser mayor a 0'); return; }
    if (form.type === 'percent' && (val <= 0 || val > 100)) {
      toast.error('El porcentaje debe estar entre 0 y 100'); return;
    }
    if (form.type === 'fixed' && val >= Number(selectedProduct.price || 0)) {
      toast.error('El descuento fijo no puede ser mayor o igual al precio del producto'); return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/products/${selectedProduct.product_id}/promotion`, {
        type: form.type,
        value: val,
        active: !!form.active,
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
      });
      toast.success('Promoción guardada');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg bg-white" data-testid="admin-promotion-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.product_id ? 'Editar promoción' : 'Nueva promoción'}</DialogTitle>
        </DialogHeader>
        {!selectedProduct ? (
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                Buscar producto
              </label>
              <Input
                data-testid="admin-promotion-product-search"
                autoFocus
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Escribe nombre, referencia o marca…"
                className="rounded-none mt-1"
              />
            </div>
            <div className="max-h-64 overflow-y-auto border border-slate-200">
              {productResults.length === 0 && (
                <div className="p-3 text-xs text-slate-500 text-center">Sin resultados</div>
              )}
              {productResults.map((p) => (
                <button
                  key={p.product_id}
                  data-testid={`admin-promotion-pick-${p.product_id}`}
                  onClick={() => setSelectedProduct(p)}
                  className="w-full text-left p-2 hover:bg-slate-50 border-b border-slate-100"
                >
                  <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.brand} · {p.reference} · {formatCOP(p.price)}</div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="font-semibold text-slate-900">{selectedProduct.name}</div>
              <div className="text-xs text-slate-500">Precio: {formatCOP(selectedProduct.price)}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Tipo</label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="admin-promotion-type" className="rounded-none mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Porcentaje (%)</SelectItem>
                    <SelectItem value="fixed">Valor fijo (COP)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  Valor {form.type === 'percent' ? '(%)' : '(COP)'}
                </label>
                <Input
                  data-testid="admin-promotion-value"
                  type="number" step="0.01" min="0"
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Desde</label>
                <Input
                  data-testid="admin-promotion-starts"
                  type="date"
                  value={form.starts_at}
                  onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Hasta</label>
                <Input
                  data-testid="admin-promotion-ends"
                  type="date"
                  value={form.ends_at}
                  onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                  className="rounded-none mt-1"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                data-testid="admin-promotion-active"
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              Promoción activa
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Cancelar</Button>
          <Button
            data-testid="admin-promotion-save"
            onClick={save}
            disabled={saving || !selectedProduct}
            className="rounded-none bg-slate-900 text-white hover:bg-slate-800"
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
